/**
 * [email-relay v1] Machine-readable contract ของโปรโตคอลรับอีเมลจาก Relay Worker
 * ต่าง Cloudflare account ผ่าน HTTPS (docs/proposals/email-relay-intake-v1/PROTOCOL.md)
 *
 * โมดูลนี้ freeze เป็น codec ของ PROTOCOL v1 ทั้งหมด: schema, canonical serialization
 * (key order ตายตัว), signature payload และค่าคงที่นโยบาย — ทีม relay (account ภายนอก)
 * บริโภค fixtures ที่ `fixtures/email-relay/` (vectors + SHA256SUMS) เป็นข้อตกลง byte-for-byte
 * ห้ามแก้ไขฟิลด์/ลำดับ key โดยไม่เพิ่ม version ใหม่ (ห้าม mutate vectors ที่ checksum แล้ว)
 *
 * แยกจาก public email SDK ขาส่ง (packages/sdk import แบบเลือกฟิลด์ — ไม่ re-export โมดูลนี้):
 * โมดูลนี้เป็น credential/transport ภายในของ intake เท่านั้น ไม่เกี่ยวกับการ verify ผู้ส่งออก
 */
import { z } from "zod";

export const EMAIL_RELAY_PROTOCOL_NAME = "uwu-email-relay";
export const EMAIL_RELAY_PROTOCOL_VERSION = 1;
/** 25 MiB — ตรงกับ INBOUND_MAX_BYTES ของ native intake (handler.ts) */
export const EMAIL_RELAY_MAX_RAW_BYTES = 26_214_400;
/** cap ของ header X-Relay-Metadata (canonical JSON, base64url) */
export const EMAIL_RELAY_MAX_METADATA_BYTES = 65_536;
/** ความสดของ sent_at เทียบเวลากลาง */
export const EMAIL_RELAY_FRESHNESS_SECONDS = 300;
/** จำนวน delivery_id สูงสุดต่อ receipt-query เดียว */
export const EMAIL_RELAY_MAX_QUERY_IDS = 100;
export const EMAIL_RELAY_HMAC_ALGORITHM = "hmac-sha256";

export const EMAIL_RELAY_SOURCE_ID = z
  .string()
  .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, "source_id ต้องเป็น [a-z0-9][a-z0-9._-]{1,62}");
export const EMAIL_RELAY_DOMAIN = z
  .string()
  .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/, "domain ต้องเป็น lowercase FQDN");
export const EMAIL_RELAY_ADDRESS = z.string().min(3).max(254).regex(/^[^\s@]+@[^\s@]+$/, "invalid address");
export const EMAIL_RELAY_NONCE = z.string().regex(/^[A-Za-z0-9_-]{22,86}$/, "nonce ต้องเป็น base64url 16..64 ไบต์");
export const EMAIL_RELAY_DELIVERY_ID = z
  .string()
  .regex(/^[A-Za-z0-9._:-]{1,128}$/, "delivery_id ต้องเป็น [A-Za-z0-9._:-]{1,128}");
export const EMAIL_RELAY_KID = z.string().regex(/^[a-z0-9][a-z0-9._-]{0,79}$/, "kid ไม่ถูกต้อง");

const iso = z.iso.datetime({ offset: true });

/** Metadata ของ intake — canonical key order ตายตัวตาม EMAIL_RELAY_INTAKE_METADATA_KEYS */
export const EmailRelayIntakeMetadata = z
  .object({
    algorithm: z.literal(EMAIL_RELAY_HMAC_ALGORITHM),
    delivery_id: EMAIL_RELAY_DELIVERY_ID,
    envelope_from: z.string().max(254),
    envelope_to: z.array(EMAIL_RELAY_ADDRESS).min(1).max(50),
    key_epoch: z.number().int().min(1).max(2_147_483_647),
    kid: EMAIL_RELAY_KID,
    message_id: z.string().max(998).nullable(),
    nonce: EMAIL_RELAY_NONCE,
    received_at: iso.nullable(),
    sent_at: iso,
    source_id: EMAIL_RELAY_SOURCE_ID,
  })
  .strict();
export type EmailRelayIntakeMetadata = z.infer<typeof EmailRelayIntakeMetadata>;

/** Metadata ของ receipt-query — canonical key order ตายตัวตาม EMAIL_RELAY_QUERY_METADATA_KEYS */
export const EmailRelayQueryMetadata = z
  .object({
    algorithm: z.literal(EMAIL_RELAY_HMAC_ALGORITHM),
    delivery_ids: z.array(EMAIL_RELAY_DELIVERY_ID).min(1).max(EMAIL_RELAY_MAX_QUERY_IDS),
    key_epoch: z.number().int().min(1).max(2_147_483_647),
    kid: EMAIL_RELAY_KID,
    nonce: EMAIL_RELAY_NONCE,
    sent_at: iso,
    source_id: EMAIL_RELAY_SOURCE_ID,
  })
  .strict();
export type EmailRelayQueryMetadata = z.infer<typeof EmailRelayQueryMetadata>;

export const EMAIL_RELAY_INTAKE_METADATA_KEYS = [
  "algorithm",
  "delivery_id",
  "envelope_from",
  "envelope_to",
  "key_epoch",
  "kid",
  "message_id",
  "nonce",
  "received_at",
  "sent_at",
  "source_id",
] as const;

export const EMAIL_RELAY_QUERY_METADATA_KEYS = [
  "algorithm",
  "delivery_ids",
  "key_epoch",
  "kid",
  "nonce",
  "sent_at",
  "source_id",
] as const;

export type EmailRelayReceiptOutcome = "accepted" | "duplicate" | "deleted";

/** Immutable transport receipt — แยกจาก business `inbound_delivery_receipts` (readiness.ts) */
export const EmailRelayReceipt = z
  .object({
    accepted_at: iso,
    delivery_id: EMAIL_RELAY_DELIVERY_ID,
    deleted_at: iso.nullable(),
    envelope_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    inbound_id: z.uuid().nullable(),
    key_epoch: z.number().int().min(1),
    nonce: EMAIL_RELAY_NONCE,
    outcome: z.enum(["accepted", "duplicate", "deleted"]),
    r2_key: z.string().min(1).max(512),
    raw_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    receipt_id: z.uuid(),
    sent_at: iso,
    size_bytes: z.number().int().min(0),
    source_id: EMAIL_RELAY_SOURCE_ID,
    state: z.enum(["stored", "deleted"]),
  })
  .strict();
export type EmailRelayReceipt = z.infer<typeof EmailRelayReceipt>;

/** ลำดับ key ของ receipt ใน canonical JSON — freeze แล้ว แก้ไม่ได้โดยไม่ bump version */
export const EMAIL_RELAY_RECEIPT_KEYS = [
  "accepted_at",
  "delivery_id",
  "deleted_at",
  "envelope_sha256",
  "inbound_id",
  "key_epoch",
  "nonce",
  "outcome",
  "r2_key",
  "raw_sha256",
  "receipt_id",
  "sent_at",
  "size_bytes",
  "source_id",
  "state",
] as const;

export const EmailRelayReceiptQueryRow = z
  .object({
    delivery_id: EMAIL_RELAY_DELIVERY_ID,
    receipt: EmailRelayReceipt.nullable(),
    status: z.enum(["found", "not_found"]),
  })
  .strict();
export type EmailRelayReceiptQueryRow = z.infer<typeof EmailRelayReceiptQueryRow>;

export const EmailRelayIntakeResponse = z
  .object({
    kind: z.enum(["intake.accepted", "intake.duplicate", "intake.deleted", "intake.unknown"]),
    nonce: EMAIL_RELAY_NONCE,
    protocol: z.literal(EMAIL_RELAY_PROTOCOL_NAME),
    receipt: EmailRelayReceipt.nullable(),
    signature: z.string().min(1).max(512),
    version: z.literal(EMAIL_RELAY_PROTOCOL_VERSION),
  })
  .strict();
export type EmailRelayIntakeResponse = z.infer<typeof EmailRelayIntakeResponse>;

export const EmailRelayQueryResponse = z
  .object({
    kind: z.literal("receipt.list"),
    nonce: EMAIL_RELAY_NONCE,
    protocol: z.literal(EMAIL_RELAY_PROTOCOL_NAME),
    results: z.array(EmailRelayReceiptQueryRow).max(EMAIL_RELAY_MAX_QUERY_IDS),
    signature: z.string().min(1).max(512),
    version: z.literal(EMAIL_RELAY_PROTOCOL_VERSION),
  })
  .strict();
export type EmailRelayQueryResponse = z.infer<typeof EmailRelayQueryResponse>;

/** Canonical JSON: JSON.stringify ของ object ที่ใส่ key ตามลำดับที่กำหนดเท่านั้น (ไม่มี whitespace) */
function canonicalJson(keys: readonly string[], values: Record<string, unknown>): string {
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = values[k];
  return JSON.stringify(out);
}

/** Canonical JSON ของ intake metadata — sender ต้องส่ง byte นี้ (base64url) ใน X-Relay-Metadata */
export function canonicalIntakeMetadataJson(meta: EmailRelayIntakeMetadata): string {
  return canonicalJson(EMAIL_RELAY_INTAKE_METADATA_KEYS, meta as unknown as Record<string, unknown>);
}

/** Canonical JSON ของ query metadata */
export function canonicalQueryMetadataJson(meta: EmailRelayQueryMetadata): string {
  return canonicalJson(EMAIL_RELAY_QUERY_METADATA_KEYS, meta as unknown as Record<string, unknown>);
}

/** Canonical JSON ของ receipt — ใช้ทั้งเซ็นตอบและ pin ใน vectors */
export function canonicalReceiptJson(receipt: EmailRelayReceipt): string {
  return canonicalJson(EMAIL_RELAY_RECEIPT_KEYS, receipt as unknown as Record<string, unknown>);
}

// NOTE (email-relay v1): โมดูลนี้เป็น pure codec เท่านั้น (package นี้ไม่มี DOM/WebCrypto types) —
// HMAC runtime, b64url และ sha256 helpers อยู่ฝั่ง worker (src/email/relay/protocol.ts) โดยอิง
// canonical/payload builders ในไฟล์นี้เป็นข้อตกลง byte-for-byte กลาง

/** Signature payload ของ request intake — ผูก canonical metadata bytes + raw hash */
export function intakeRequestSignaturePayload(metadataB64url: string, rawSha256Hex: string): string {
  return `${EMAIL_RELAY_PROTOCOL_NAME}-v${EMAIL_RELAY_PROTOCOL_VERSION}\nintake\n${metadataB64url}\n${rawSha256Hex}`;
}

/** Signature payload ของ request receipt-query (ไม่มี raw body) */
export function queryRequestSignaturePayload(metadataB64url: string): string {
  return `${EMAIL_RELAY_PROTOCOL_NAME}-v${EMAIL_RELAY_PROTOCOL_VERSION}\nreceipt-query\n${metadataB64url}`;
}

/** Signature payload ของ response — nonce-bound (ผูกกับ nonce ของ request อินสแตนซ์นั้น) */
export function responseSignaturePayload(kind: string, requestNonce: string, canonicalBody: string): string {
  return `${EMAIL_RELAY_PROTOCOL_NAME}-v${EMAIL_RELAY_PROTOCOL_VERSION}\n${kind}\n${requestNonce}\n${canonicalBody}`;
}

export function formatRelaySignature(kid: string, keyEpoch: number, sigB64url: string): string {
  return `v1=${kid}:${keyEpoch}:${sigB64url}`;
}

export function parseRelaySignature(header: string): { kid: string; keyEpoch: number; sig: string } | null {
  const m = /^v1=([A-Za-z0-9._-]{1,80}):([1-9][0-9]{0,8}):([A-Za-z0-9_-]{43,128})$/.exec(header.trim());
  if (!m) return null;
  return { kid: m[1]!, keyEpoch: Number(m[2]), sig: m[3]! };
}

export type EmailRelayImmutableMeta = {
  readonly rawSha256: string;
  readonly envelopeSha256: string;
  readonly sizeBytes: number;
};

/** Immutable metadata = raw hash + canonical envelope hash + size — ต่างกันที่ไหน = conflict 409 */
export function sameImmutableMeta(a: EmailRelayImmutableMeta, b: EmailRelayImmutableMeta): boolean {
  return a.rawSha256 === b.rawSha256 && a.envelopeSha256 === b.envelopeSha256 && a.sizeBytes === b.sizeBytes;
}

/** canonical envelope string ที่ใช้คำนวณ envelope_sha256 — worker เอาไป sha256 ต่อ (runtime crypto อยู่ฝั่ง worker) */
export function canonicalEnvelopeJson(envelopeFrom: string, envelopeTo: readonly string[]): string {
  return JSON.stringify([envelopeFrom.toLowerCase(), envelopeTo.map((t) => t.toLowerCase())]);
}

/** Response body builder — wrapper key order ตายตัว (protocol, version, kind, nonce, body, signature) */
export function intakeResponseBody(
  kind: EmailRelayIntakeResponse["kind"],
  requestNonce: string,
  receipt: EmailRelayReceipt | null,
  signature: string,
): EmailRelayIntakeResponse {
  return { protocol: EMAIL_RELAY_PROTOCOL_NAME, version: EMAIL_RELAY_PROTOCOL_VERSION, kind, nonce: requestNonce, receipt, signature };
}

export function queryResponseBody(
  results: EmailRelayReceiptQueryRow[],
  requestNonce: string,
  signature: string,
): EmailRelayQueryResponse {
  return { protocol: EMAIL_RELAY_PROTOCOL_NAME, version: EMAIL_RELAY_PROTOCOL_VERSION, kind: "receipt.list", nonce: requestNonce, results, signature };
}

/** Canonical JSON ของ query results (ใช้เป็น canonical body ตอนเซ็น) */
export function canonicalQueryResultsJson(results: EmailRelayReceiptQueryRow[]): string {
  return JSON.stringify(
    results.map((r) =>
      r.receipt === null
        ? { delivery_id: r.delivery_id, receipt: null, status: r.status }
        : { delivery_id: r.delivery_id, receipt: JSON.parse(canonicalReceiptJson(r.receipt)), status: r.status },
    ),
  );
}

// ---- admin API (ภายใน — ไม่ใช่ wire protocol ของ relay; /api/v1/admin/email-relay/*) ----

export const EmailRelaySourceCreate = z
  .object({
    source_id: EMAIL_RELAY_SOURCE_ID,
    label: z.string().trim().min(1).max(200),
    allowed_domains: z.array(EMAIL_RELAY_DOMAIN).max(50),
    accepted_spool_epochs: z.array(z.uuid()).max(10).default([]),
  })
  .strict();
export type EmailRelaySourceCreate = z.infer<typeof EmailRelaySourceCreate>;

export const EmailRelayDomainPatch = z
  .object({ add: z.array(EMAIL_RELAY_DOMAIN).max(50), remove: z.array(EMAIL_RELAY_DOMAIN).max(50) })
  .strict();
export type EmailRelayDomainPatch = z.infer<typeof EmailRelayDomainPatch>;

export const EmailRelayReconcileRequest = z.object({ delivery_id: EMAIL_RELAY_DELIVERY_ID }).strict();
export type EmailRelayReconcileRequest = z.infer<typeof EmailRelayReconcileRequest>;

export const EmailRelaySourceOut = z
  .object({
    source_id: EMAIL_RELAY_SOURCE_ID,
    label: z.string(),
    allowed_domains: z.array(EMAIL_RELAY_DOMAIN),
    accepted_spool_epochs: z.array(z.uuid()),
    status: z.enum(["active", "paused", "revoked"]),
    key_seq: z.number().int().min(0),
    paused_at: iso.nullable(),
    revoked_at: iso.nullable(),
    created_at: iso,
    updated_at: iso,
  })
  .strict();
export type EmailRelaySourceOut = z.infer<typeof EmailRelaySourceOut>;

export const EmailRelayKeyOut = z
  .object({
    kid: EMAIL_RELAY_KID,
    key_epoch: z.number().int().min(1),
    fingerprint: z.string().regex(/^[0-9a-f]{16}$/),
    status: z.enum(["active", "retired"]),
    created_at: iso,
    retired_at: iso.nullable(),
  })
  .strict();
export type EmailRelayKeyOut = z.infer<typeof EmailRelayKeyOut>;

/** mint — `secret` แสดงครั้งเดียว (เก็บเข้า RELAY_KEYRING ฝั่ง deploy แล้วห้ามถามกลับ) */
export const EmailRelayKeyMinted = EmailRelayKeyOut.extend({ secret: z.string().min(1).max(200) }).strict();
export type EmailRelayKeyMinted = z.infer<typeof EmailRelayKeyMinted>;

export const EmailRelayReceiptSummary = z
  .object({
    receipt_id: z.uuid(),
    delivery_id: EMAIL_RELAY_DELIVERY_ID,
    inbound_id: z.uuid().nullable(),
    raw_sha256: z.string().regex(/^[0-9a-f]{64}$/),
    size_bytes: z.number().int().min(0),
    accepted_at: iso,
    deleted_at: iso.nullable(),
  })
  .strict();
export type EmailRelayReceiptSummary = z.infer<typeof EmailRelayReceiptSummary>;

export const EmailRelayStats = z
  .object({
    sources_active: z.number().int(),
    receipts_total: z.number().int(),
    receipts_deleted: z.number().int(),
    /** intake แถว queued ที่ยังไม่ publish เข้า queue (cron inbound_intake_relay กำลังกู้) */
    intake_publish_pending: z.number().int(),
  })
  .strict();
export type EmailRelayStats = z.infer<typeof EmailRelayStats>;
